import React, { useEffect, useMemo, useRef } from "react";

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

export default function OtpInput({ value, onChange, length = 6, disabled = false, autoFocus = true, onComplete }) {
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    const cleaned = onlyDigits(value).slice(0, length);
    return Array.from({ length }, (_, i) => cleaned[i] || "");
  }, [value, length]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const idx = digits.findIndex((d) => !d);
    inputsRef.current[idx === -1 ? length - 1 : idx]?.focus?.();
  }, [autoFocus, disabled, length]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (nextDigits, focusIndex) => {
    const next = nextDigits.join("").slice(0, length);
    onChange(next);
    if (next.length === length) onComplete?.(next);
    if (typeof focusIndex === "number") {
      inputsRef.current[focusIndex]?.focus?.();
      inputsRef.current[focusIndex]?.select?.();
    }
  };

  const handleChangeAt = (index, raw) => {
    const next = onlyDigits(raw);
    const nextDigits = [...digits];
    if (!next) {
      nextDigits[index] = "";
      return commit(nextDigits, index);
    }
    const chars = next.slice(0, length - index).split("");
    chars.forEach((c, offset) => { nextDigits[index + offset] = c; });
    commit(nextDigits, Math.min(index + chars.length, length - 1));
  };

  const handleKeyDownAt = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const nextDigits = [...digits];
      nextDigits[index - 1] = "";
      commit(nextDigits, index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus?.();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus?.();
    }
  };

  const handlePaste = (e) => {
    const pasted = onlyDigits(e.clipboardData?.getData("text") || "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    commit(pasted.split(""), Math.min(pasted.length, length - 1));
  };

  return (
    <div className="flex items-center justify-center gap-2" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={length}
          disabled={disabled}
          value={d}
          onChange={(e) => handleChangeAt(i, e.target.value)}
          onKeyDown={(e) => handleKeyDownAt(i, e)}
          onFocus={(e) => e.target.select()}
          className="h-[46px] w-[46px] rounded-[10px] border border-[#cccccc] bg-[rgba(234,231,231,0.5)] text-center text-[18px] font-semibold text-[#3d3935] outline-none transition duration-150 ease-in-out focus:border-[#212529] focus:shadow-[0_0_0_3px_rgba(0,0,0,0.08)] disabled:opacity-60"
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
